import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type { CompareItem, Product } from '@/types';
import { useNotifications } from '@/composables/useNotifications';

export const useCompareStore = defineStore('compare', () => {
  const items = ref<CompareItem[]>([]);
  const maxItems = 4; // Maximum products to compare
  
  const { showNotification } = useNotifications();

  const compareCount = computed(() => items.value.length);
  
  const isInCompare = (productId: string) => {
    return items.value.some(item => item.productId === productId);
  };

  const canAddMore = computed(() => items.value.length < maxItems);

  const addToCompare = async (product: Product) => {
    try {
      if (isInCompare(product.id)) {
        showNotification('El producto ya está en comparación', 'info');
        return;
      }

      if (!canAddMore.value) {
        showNotification(`Solo puedes comparar hasta ${maxItems} productos`, 'warning');
        return;
      }

      const compareItem: CompareItem = {
        id: Date.now().toString(),
        productId: product.id,
        product,
        addedAt: new Date(),
      };

      items.value.push(compareItem);
      await saveCompare();
      showNotification(`${product.name} añadido a comparación`, 'success');
    } catch (error) {
      showNotification('Error al añadir a comparación', 'error');
    }
  };

  const removeFromCompare = async (productId: string) => {
    try {
      const itemIndex = items.value.findIndex(item => item.productId === productId);
      if (itemIndex >= 0) {
        const itemName = items.value[itemIndex].product.name;
        items.value.splice(itemIndex, 1);
        await saveCompare();
        showNotification(`${itemName} eliminado de comparación`, 'success');
      }
    } catch (error) {
      showNotification('Error al eliminar de comparación', 'error');
    }
  };

  const clearCompare = async () => {
    try {
      items.value = [];
      await saveCompare();
      showNotification('Comparación vaciada', 'success');
    } catch (error) {
      showNotification('Error al vaciar comparación', 'error');
    }
  };

  const saveCompare = async () => {
    const compareData = {
      items: items.value,
    };
    localStorage.setItem('compare', JSON.stringify(compareData));
  };

  const loadCompare = () => {
    try {
      const compareData = localStorage.getItem('compare');
      if (compareData) {
        const parsed = JSON.parse(compareData);
        items.value = parsed.items?.map((item: any) => ({
          ...item,
          addedAt: new Date(item.addedAt),
        })) || [];
      }
    } catch (error) {
      console.error('Error loading compare data:', error);
    }
  };

  return {
    items: computed(() => items.value),
    compareCount,
    maxItems,
    isInCompare,
    canAddMore,
    addToCompare,
    removeFromCompare,
    clearCompare,
    loadCompare,
  };
});